import React, { useState, useEffect, useCallback } from 'react';
import {
    Autocomplete,
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    CircularProgress,
    FormControl, FormControlLabel, IconButton, InputLabel, MenuItem,
    Select, Switch, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GlobalTip from '../../providers/GlobalTip';
import ClientService from '../../services/ClientService';
import OrbitMetadataService from '../../services/OrbitMetadataService';
import SectionTitle from '../../components/common/SectionTitle';

// ─── Constants ───────────────────────────────────────────────────────────────

const PROMPT_TYPES = ['m1', 'client'];
const ICON_NAMES = ['warning', 'shopping', 'list', 'chart', 'location', 'star', 'custom'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function PromptTypeChip({ type }) {
    return (
        <Chip
            label={type === 'm1' ? 'M1 Platform' : 'Client'}
            size="small"
            color={type === 'm1' ? 'primary' : 'default'}
            variant="outlined"
        />
    );
}

function clientOptionLabel(option) {
    const id = option?.Id ?? option?.id ?? option?.client_id ?? '';
    const code = option?.ClientCode ?? option?.client_code ?? option?.Code ?? '';
    const name = option?.Name ?? option?.name ?? option?.client_name ?? '';
    return [code ? `[${code}]` : '', name, id ? `(#${id})` : ''].filter(Boolean).join(' ').trim();
}

function toDisplayText(value, fallback = '—') {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') {
        const normalized = value.trim();
        return normalized === '' ? fallback : normalized;
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return fallback;
}

function truncateText(value, max = 180) {
    const text = toDisplayText(value);
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function formatDateTime(value) {
    const text = toDisplayText(value);
    if (text === '—') return text;
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? text : parsed.toLocaleString();
}

function ChatFeedbackStat({ label, value, color = '#111827' }) {
    return (
        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, minWidth: 160, bgcolor: '#fff' }}>
            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.4, textTransform: 'uppercase' }}>
                {label}
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700, color }}>
                {value}
            </Typography>
        </Box>
    );
}

// ─── Prompt Form Dialog ───────────────────────────────────────────────────────

const EMPTY_PROMPT = {
    prompt_type: 'm1',
    client_id: '',
    label: '',
    icon_bg_color: '',
    icon_name: '',
    icon_stroke_color: '',
    sort_order: 0,
    is_active: true,
};

function PromptDialog({ open, onClose, onSaved, initial }) {
    const [form, setForm] = useState(EMPTY_PROMPT);
    const [saving, setSaving] = useState(false);
    const [clientOptions, setClientOptions] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const TipContext = GlobalTip.useMessage();
    const isEdit = Boolean(initial?.id);

    useEffect(() => {
        if (open) {
            setForm(initial ? {
                prompt_type:       initial.prompt_type       || 'm1',
                client_id:         initial.client_id         || '',
                label:             initial.label             || '',
                icon_bg_color:     initial.icon_bg_color     || '',
                icon_name:         initial.icon_name         || '',
                icon_stroke_color: initial.icon_stroke_color || '',
                sort_order:        initial.sort_order        ?? 0,
                is_active:         initial.is_active         ?? true,
            } : EMPTY_PROMPT);
        }
    }, [open, initial]);

    useEffect(() => {
        let cancelled = false;
        if (!open || form.prompt_type !== 'client' || clientOptions.length > 0) {
            return undefined;
        }

        setClientsLoading(true);
        ClientService.listClientsSimple()
            .then((data) => {
                if (!cancelled) {
                    setClientOptions(Array.isArray(data) ? data : []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setClientOptions([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setClientsLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [open, form.prompt_type, clientOptions.length]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const setBool = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.checked }));

    const handleSave = async () => {
        if (!form.label.trim()) {
            TipContext.show('Prompt label is required', 'error');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                client_id: form.client_id !== '' ? Number(form.client_id) : null,
                sort_order: Number(form.sort_order),
            };
            if (isEdit) {
                await OrbitMetadataService.updatePrompt(initial.id, payload);
                TipContext.show('Prompt updated successfully', 'success');
            } else {
                await OrbitMetadataService.createPrompt(payload);
                TipContext.show('Prompt created successfully', 'success');
            }
            onSaved();
        } catch {
            TipContext.show('Failed to save prompt', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit' : 'New'} Orbit Prompt</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                <FormControl fullWidth size="small">
                    <InputLabel>Prompt Type</InputLabel>
                    <Select value={form.prompt_type} label="Prompt Type" onChange={set('prompt_type')}>
                        {PROMPT_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>{t === 'm1' ? 'M1 Platform (global)' : 'Client-specific'}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {form.prompt_type === 'client' && (
                    <Autocomplete
                        size="small"
                        options={clientOptions}
                        loading={clientsLoading}
                        value={clientOptions.find((option) => String(option?.Id ?? option?.id ?? '') === String(form.client_id ?? '')) ?? null}
                        onChange={(_, value) => setForm((f) => ({
                            ...f,
                            client_id: value ? (value.Id ?? value.id ?? '') : '',
                        }))}
                        isOptionEqualToValue={(option, value) => String(option?.Id ?? option?.id ?? '') === String(value?.Id ?? value?.id ?? '')}
                        getOptionLabel={clientOptionLabel}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Client"
                                helperText="Search by client name or code"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {clientsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                )}

                <TextField
                    label="Prompt Label *"
                    size="small"
                    multiline
                    minRows={2}
                    value={form.label}
                    onChange={set('label')}
                    placeholder="e.g. What's working and what needs attention?"
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Icon Name</InputLabel>
                        <Select value={form.icon_name} label="Icon Name" onChange={set('icon_name')}>
                            <MenuItem value=""><em>None</em></MenuItem>
                            {ICON_NAMES.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Icon BG Color"
                        size="small"
                        fullWidth
                        value={form.icon_bg_color}
                        onChange={set('icon_bg_color')}
                        placeholder="#FEF3F2"
                        InputProps={{
                            endAdornment: form.icon_bg_color ? (
                                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: form.icon_bg_color, border: '1px solid #ddd', flexShrink: 0 }} />
                            ) : null,
                        }}
                    />
                    <TextField
                        label="Icon Stroke"
                        size="small"
                        fullWidth
                        value={form.icon_stroke_color}
                        onChange={set('icon_stroke_color')}
                        placeholder="#B42318"
                        InputProps={{
                            endAdornment: form.icon_stroke_color ? (
                                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: form.icon_stroke_color, border: '1px solid #ddd', flexShrink: 0 }} />
                            ) : null,
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        label="Sort Order"
                        size="small"
                        type="number"
                        sx={{ width: 120 }}
                        value={form.sort_order}
                        onChange={set('sort_order')}
                    />
                    <FormControlLabel
                        control={<Switch checked={form.is_active} onChange={setBool('is_active')} color="success" />}
                        label="Active"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Prompts Tab ──────────────────────────────────────────────────────────────

function PromptsTab() {
    const [prompts, setPrompts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [filterType, setFilterType] = useState('');
    const TipContext = GlobalTip.useMessage();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await OrbitMetadataService.listPrompts(filterType ? { prompt_type: filterType } : {});
            setPrompts(res?.data || []);
        } catch {
            TipContext.show('Failed to load prompts', 'error');
        } finally {
            setLoading(false);
        }
    }, [filterType]); 

    useEffect(() => { load(); }, [load]);

    const handleEdit = (row) => { setEditTarget(row); setDialogOpen(true); };
    const handleNew  = () => { setEditTarget(null); setDialogOpen(true); };
    const handleClose = () => { setDialogOpen(false); setEditTarget(null); };
    const handleSaved = () => { handleClose(); load(); };

    const handleDelete = async (row) => {
        if (!window.confirm(`Delete prompt "${row.label}"?`)) return;
        try {
            await OrbitMetadataService.deletePrompt(row.id);
            TipContext.show('Prompt deleted', 'success');
            load();
        } catch {
            TipContext.show('Failed to delete prompt', 'error');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Filter by type</InputLabel>
                    <Select value={filterType} label="Filter by type" onChange={(e) => setFilterType(e.target.value)}>
                        <MenuItem value="">All types</MenuItem>
                        <MenuItem value="m1">M1 Platform</MenuItem>
                        <MenuItem value="client">Client-specific</MenuItem>
                    </Select>
                </FormControl>
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
                    Add Prompt
                </Button>
            </Box>

            {loading ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Loading…</Typography>
            ) : prompts.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No prompts found.</Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                                {['#', 'Type', 'Label', 'Icon', 'Order', 'Status', 'Actions'].map((h) => (
                                    <th key={h} style={{ padding: '8px 12px', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {prompts.map((row) => (
                                <tr key={row.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '8px 12px', color: '#999' }}>{row.id}</td>
                                    <td style={{ padding: '8px 12px' }}><PromptTypeChip type={row.prompt_type} /></td>
                                    <td style={{ padding: '8px 12px', maxWidth: 340 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {row.icon_bg_color && (
                                                <Box sx={{
                                                    width: 22, height: 22, borderRadius: '50%',
                                                    bgcolor: row.icon_bg_color, border: '1px solid #ddd',
                                                    flexShrink: 0,
                                                }} />
                                            )}
                                            <Typography variant="body2">{row.label}</Typography>
                                        </Box>
                                    </td>
                                    <td style={{ padding: '8px 12px', color: '#777' }}>{row.icon_name || '—'}</td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{row.sort_order}</td>
                                    <td style={{ padding: '8px 12px' }}>
                                        <Chip size="small" label={row.is_active ? 'Active' : 'Inactive'}
                                            color={row.is_active ? 'success' : 'default'} variant="outlined" />
                                    </td>
                                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                                        <Tooltip title="Edit"><IconButton size="small" onClick={() => handleEdit(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(row)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Box>
            )}

            <PromptDialog open={dialogOpen} onClose={handleClose} onSaved={handleSaved} initial={editTarget} />
        </Box>
    );
}

// ─── Chat Feedback Tab ────────────────────────────────────────────────────────

const CHAT_FEEDBACK_FILTERS = {
    client_id: '',
    thumbs: '',
    needs_review: '',
    conversation_id: '',
    limit: 50,
};

function ChatFeedbackTab() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({ total: 0, positive: 0, negative: 0, with_text: 0, review_required: 0 });
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(CHAT_FEEDBACK_FILTERS);
    const TipContext = GlobalTip.useMessage();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await OrbitMetadataService.listChatFeedback({
                client_id: filters.client_id !== '' ? Number(filters.client_id) : undefined,
                thumbs: filters.thumbs !== '' ? Number(filters.thumbs) : undefined,
                needs_review: filters.needs_review === '' ? undefined : filters.needs_review === 'true',
                conversation_id: filters.conversation_id || undefined,
                limit: Number(filters.limit) || 50,
            });
            const payload = res?.data || res || {};
            setRows(Array.isArray(payload.feedback) ? payload.feedback : []);
            setSummary({
                total: payload?.summary?.total ?? payload?.total ?? 0,
                positive: payload?.summary?.positive ?? 0,
                negative: payload?.summary?.negative ?? 0,
                with_text: payload?.summary?.with_text ?? 0,
                review_required: payload?.summary?.review_required ?? 0,
            });
        } catch {
            setRows([]);
            setSummary({ total: 0, positive: 0, negative: 0, with_text: 0, review_required: 0 });
            TipContext.show('Failed to load chat feedback', 'error');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    const setFilter = (key) => (event) => setFilters((current) => ({ ...current, [key]: event.target.value }));
    const handleReset = () => setFilters(CHAT_FEEDBACK_FILTERS);

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review thumbs-up / thumbs-down responses captured from Orbit AI conversations, including negative responses that should be investigated.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <ChatFeedbackStat label="Total feedback" value={summary.total || 0} />
                <ChatFeedbackStat label="Positive" value={summary.positive || 0} color="#027A48" />
                <ChatFeedbackStat label="Negative" value={summary.negative || 0} color="#B42318" />
                <ChatFeedbackStat label="Needs review" value={summary.review_required || 0} color="#B42318" />
                <ChatFeedbackStat label="With comment" value={summary.with_text || 0} color="#175CD3" />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                <TextField
                    label="Client ID"
                    size="small"
                    type="number"
                    value={filters.client_id}
                    onChange={setFilter('client_id')}
                    sx={{ width: 140 }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Feedback</InputLabel>
                    <Select value={filters.thumbs} label="Feedback" onChange={setFilter('thumbs')}>
                        <MenuItem value="">All feedback</MenuItem>
                        <MenuItem value="1">Thumbs up</MenuItem>
                        <MenuItem value="-1">Thumbs down</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel>Review status</InputLabel>
                    <Select value={filters.needs_review} label="Review status" onChange={setFilter('needs_review')}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Needs review</MenuItem>
                        <MenuItem value="false">No review flag</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    label="Conversation ID"
                    size="small"
                    value={filters.conversation_id}
                    onChange={setFilter('conversation_id')}
                    sx={{ minWidth: 240, flex: '1 1 240px' }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Rows</InputLabel>
                    <Select value={filters.limit} label="Rows" onChange={setFilter('limit')}>
                        {[25, 50, 100].map((value) => (
                            <MenuItem key={value} value={value}>{value}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="outlined" onClick={load}>Refresh</Button>
                <Button onClick={handleReset}>Reset</Button>
            </Box>

            {loading ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Loading…</Typography>
            ) : rows.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No chat feedback found for the selected filters.</Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                                {['Submitted', 'Feedback', 'Client', 'Conversation', 'User Prompt', 'Assistant Response', 'Comment'].map((h) => (
                                    <th key={h} style={{ padding: '8px 12px', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.feedback_id} style={{ borderBottom: '1px solid #f0f0f0', verticalAlign: 'top' }}>
                                    <td style={{ padding: '8px 12px', minWidth: 150, color: '#555' }}>
                                        <Typography variant="body2">{formatDateTime(row.created_at)}</Typography>
                                    </td>
                                    <td style={{ padding: '8px 12px', minWidth: 130 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                            <Chip
                                                size="small"
                                                color={row.thumbs > 0 ? 'success' : 'error'}
                                                variant="outlined"
                                                label={row.thumbs > 0 ? 'Thumbs up' : 'Thumbs down'}
                                                sx={{ width: 'fit-content' }}
                                            />
                                            {row.needs_review ? (
                                                <Chip size="small" color="warning" variant="outlined" label="Needs review" sx={{ width: 'fit-content' }} />
                                            ) : null}
                                        </Box>
                                    </td>
                                    <td style={{ padding: '8px 12px', minWidth: 180 }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {toDisplayText(row.client_code, `Client #${toDisplayText(row.client_id)}`)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {toDisplayText(row.client_name)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Client ID: {toDisplayText(row.client_id)}
                                        </Typography>
                                    </td>
                                    <td style={{ padding: '8px 12px', minWidth: 220 }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                            {toDisplayText(row.conversation_id)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {toDisplayText(row.request_type)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Status: {toDisplayText(row.conversation_status)}
                                        </Typography>
                                    </td>
                                    <td style={{ padding: '8px 12px', maxWidth: 280 }}>
                                        <Typography variant="body2">{truncateText(row.user_prompt_preview, 220)}</Typography>
                                    </td>
                                    <td style={{ padding: '8px 12px', maxWidth: 320 }}>
                                        <Typography variant="body2">{truncateText(row.assistant_response_preview, 260)}</Typography>
                                    </td>
                                    <td style={{ padding: '8px 12px', maxWidth: 260 }}>
                                        <Typography variant="body2">{truncateText(row.feedback_text, 220)}</Typography>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Box>
            )}
        </Box>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function OrbitMetadataManagement() {
    const [tab, setTab] = useState(0);

    useEffect(() => {
        document.title = 'Retail ORBIT: Orbit AI Metadata';
    }, []);

    return (
        <Box sx={{ p: 3 }}>
            <SectionTitle title="Orbit AI — Application Metadata" />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage platform-level Orbit prompts and review recorded Orbit AI chat feedback across clients.
                Prompt changes take effect immediately for all active clients.
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label="Orbit Prompts" />
                    <Tab label="Chat Feedback" />
                </Tabs>
            </Box>

            {tab === 0 && <PromptsTab />}
            {tab === 1 && <ChatFeedbackTab />}
        </Box>
    );
}

